import { format, subMonths } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Brain,
  Bug,
  Building2,
  CheckCircle,
  ChevronRight,
  Clock,
  Download,
  Edit,
  Eye,
  FileSpreadsheet,
  FileText,
  Filter,
  Gauge,
  PieChart,
  Play,
  Plus,
  RefreshCw,
  ScatterChart,
  Settings,
  Shield,
  Target,
  TrendingDown,
  TrendingUp,
  XCircle,
  Zap
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  LineChart,
  Pie,
  PieChart as RechartsPieChart,
  ScatterChart as RechartsScatterChart,
  Scatter,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import api from '../../api';
import { useProject } from '../../contexts/ProjectContext';
import toastService from '../../services/toastService';
import { AddControlModal } from '../RCSA/AddControlModal';
import ComprehensiveExportDialog from '../Export/ComprehensiveExportDialog';

interface RiskMetrics {
  total_risks: number;
  critical_risks: number;
  high_risks: number;
  medium_risks: number;
  low_risks: number;
  risk_trend: 'increasing' | 'decreasing' | 'stable';
  risk_appetite_breaches: number;
  avg_risk_score: number;
}

interface ControlMetrics {
  total_controls: number;
  effective_controls: number;
  ineffective_controls: number;
  controls_needing_review: number;
  control_effectiveness_avg: number;
  controls_tested_last_30_days: number;
}

interface ComplianceMetrics {
  compliance_score: number;
  regulatory_violations: number;
  audit_findings_open: number;
  certification_status: string;
  next_audit_date: string;
  policies_updated_last_quarter: number;
}

interface BusinessMetrics {
  business_units_assessed: number;
  total_business_units: number;
  processes_mapped: number;
  employees_trained: number;
  incidents_reported: number;
  incident_trend: 'improving' | 'worsening' | 'stable';
}

interface AlertItem {
  id: string;
  type: 'risk_breach' | 'control_failure' | 'compliance_issue' | 'audit_finding';
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  business_unit: string;
  created_at: string;
  status: 'open' | 'investigating' | 'resolved';
}

interface ControlStatus {
  id: string;
  control_name: string;
  control_type: string;
  design_effectiveness: string;
  operating_effectiveness: string;
  design_effectiveness_score: number;
  operating_effectiveness_score: number;
  last_tested_date: string;
  next_testing_due: string;
  testing_frequency: string;
  current_status: 'Effective' | 'Partially Effective' | 'Ineffective' | 'Not Tested';
  owner: string;
  process_step_name: string;
  risk_count: number;
  gaps: ControlGap[];
  testing_history: TestingHistory[];
  remediation_actions: RemediationAction[];
}

interface ControlGap {
  id: string;
  gap_type: string;
  description: string;
  severity: 'High' | 'Medium' | 'Low';
  identified_date: string;
  status: 'Open' | 'In Progress' | 'Closed';
  remediation_plan: string;
  target_resolution_date: string;
  assigned_to: string;
}

interface TestingHistory {
  id: string;
  test_date: string;
  test_type: string;
  result: string;
  effectiveness_score: number;
  findings: string;
  tested_by: string;
}

interface RemediationAction {
  id: string;
  title: string;
  description: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Open' | 'In Progress' | 'Completed';
  assigned_to: string;
  due_date: string;
  created_date: string;
}

interface ProcessMetrics {
  total_controls: number;
  effective_controls: number;
  testing_overdue: number;
  open_gaps: number;
  pending_actions: number;
  avg_effectiveness_score: number;
  trend_direction: 'up' | 'down' | 'stable';
  trend_percentage: number;
}

interface RiskAnalytics {
  risk_distribution: Array<{
    category: string;
    count: number;
    avg_score: number;
    trend: number;
  }>;
  risk_trends: Array<{
    date: string;
    high_risks: number;
    medium_risks: number;
    low_risks: number;
    avg_score: number;
  }>;
  correlation_matrix: Array<{
    risk_a: string;
    risk_b: string;
    correlation: number;
    significance: number;
  }>;
  risk_heat_map: Array<{
    likelihood: number;
    impact: number;
    count: number;
    risk_score: number;
  }>;
  scenario_analysis: Array<{
    scenario: string;
    probability: number;
    impact_range: [number, number];
    risk_change: number;
  }>;
  monte_carlo_results: {
    mean: number;
    std_dev: number;
    percentiles: { [key: string]: number };
    iterations: number;
  };
  risk_appetite_analysis: {
    current_position: number;
    appetite_threshold: number;
    variance: number;
    trend: string;
  };
  predictive_models: Array<{
    model_name: string;
    accuracy: number;
    predictions: Array<{
      period: string;
      predicted_risks: number;
      confidence_interval: [number, number];
    }>;
  }>;
}

interface RiskModelingParams {
  time_horizon: number;
  confidence_level: number;
  iterations: number;
  scenario_type: string;
  correlation_threshold: number;
  volatility_adjustment: number;
}

interface ExecutiveDashboardProps {
  projectId?: number;
  timeframe?: '30d' | '90d' | '1y';
}

export const ExecutiveDashboard: React.FC<ExecutiveDashboardProps> = ({
  projectId: propProjectId,
  timeframe = '30d'
}) => {
  const { currentProject } = useProject();

  // Use prop projectId if provided, otherwise get from context
  const projectId = propProjectId || currentProject?.id;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState(timeframe);
  const [selectedView, setSelectedView] = useState<'overview' | 'risks' | 'controls' | 'compliance' | 'testing' | 'gaps' | 'trends' | 'risk_analytics' | 'correlation' | 'scenario' | 'predictive' | 'exports'>('overview');
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  // Metrics state
  const [riskMetrics, setRiskMetrics] = useState<RiskMetrics | null>(null);
  const [controlMetrics, setControlMetrics] = useState<ControlMetrics | null>(null);
  const [complianceMetrics, setComplianceMetrics] = useState<ComplianceMetrics | null>(null);
  const [businessMetrics, setBusinessMetrics] = useState<BusinessMetrics | null>(null);
  const [alertItems, setAlertItems] = useState<AlertItem[]>([]);

  // Chart data
  const [riskTrendData, setRiskTrendData] = useState<any[]>([]);
  const [riskDistributionData, setRiskDistributionData] = useState<any[]>([]);

  // Process Owner Dashboard state
  const [controls, setControls] = useState<ControlStatus[]>([]);
  const [processMetrics, setProcessMetrics] = useState<ProcessMetrics | null>(null);
  const [selectedControl, setSelectedControl] = useState<ControlStatus | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [testingDialogOpen, setTestingDialogOpen] = useState(false);
  const [gapDialogOpen, setGapDialogOpen] = useState(false);
  const [addControlModalOpen, setAddControlModalOpen] = useState(false);

  // Risk Expert Dashboard state
  const [riskAnalytics, setRiskAnalytics] = useState<RiskAnalytics | null>(null);
  const [modelingParams, setModelingParams] = useState<RiskModelingParams>({
    time_horizon: 12,
    confidence_level: 95,
    iterations: 10000,
    scenario_type: 'base',
    correlation_threshold: 0.5,
    volatility_adjustment: 1.0
  });
  const [modelingDialogOpen, setModelingDialogOpen] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(['risk_score', 'likelihood', 'impact']);
  const [dateRange, setDateRange] = useState({
    start: format(subMonths(new Date(), 6), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });

  // Export state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [defaultExportType, setDefaultExportType] = useState('comprehensive_rcsa');

  useEffect(() => {
    fetchDashboardData();
  }, [projectId, selectedTimeframe, selectedView]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      console.log('📊 Loading executive dashboard data for project:', projectId);

      // Build common params
      const commonParams = {
        timeframe: selectedTimeframe,
        ...(projectId && { project_id: projectId })
      };

      const [
        riskResponse,
        controlResponse,
        complianceResponse,
        businessResponse,
        alertsResponse
      ] = await Promise.all([
        api.get('/api/executive-dashboard/risk-metrics', {
          params: commonParams
        }),
        api.get('/api/executive-dashboard/control-metrics', {
          params: commonParams
        }),
        api.get('/api/executive-dashboard/compliance-metrics', {
          params: commonParams
        }),
        api.get('/api/executive-dashboard/business-metrics', {
          params: { ...commonParams }
        }),
        api.get('/api/executive-dashboard/alerts', {
          params: {
            limit: 10,
            ...(projectId && { project_id: projectId })
          }
        })
      ]);

      setRiskMetrics(riskResponse.data);
      setControlMetrics(controlResponse.data);
      setComplianceMetrics(complianceResponse.data);
      setBusinessMetrics(businessResponse.data);
      setAlertItems(alertsResponse.data.alerts || []);

      // Set chart data if available
      if (riskResponse.data.trend_data) {
        setRiskTrendData(riskResponse.data.trend_data);
      }
      if (riskResponse.data.distribution_data) {
        setRiskDistributionData(riskResponse.data.distribution_data);
      }

      // Fetch Process Owner data for new tabs
      if (selectedView === 'testing' || selectedView === 'gaps' || selectedView === 'trends') {
        await fetchProcessOwnerData();
      }

      // Fetch Risk Expert data for new tabs
      if (selectedView === 'risk_analytics' || selectedView === 'correlation' || selectedView === 'scenario' || selectedView === 'predictive') {
        await fetchRiskExpertAnalytics();
      }

      console.log('✅ Executive dashboard data loaded successfully');

    } catch (error) {
      console.error('Failed to load executive dashboard data:', error);

      // More detailed error messaging
      if (error instanceof Error) {
        toastService.error(`Failed to load dashboard data: ${error.message}`);
      } else {
        toastService.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessOwnerData = async () => {
    try {
      if (!projectId) return;

      const [controlsResponse, metricsResponse] = await Promise.all([
        api.get(`/api/process-owner-dashboard/controls/${projectId}`),
        api.get(`/api/process-owner-dashboard/metrics/${projectId}`)
      ]);

      const dashboardControls = controlsResponse.data?.controls || [];
      const dashboardMetrics = metricsResponse.data?.metrics || null;

      const realControls: ControlStatus[] = dashboardControls.map((control: any) => ({
        id: control.id.toString(),
        control_name: control.control_name || 'Unnamed Control',
        control_type: control.control_type || 'General',
        design_effectiveness: control.design_effectiveness || 'Not Assessed',
        operating_effectiveness: control.operating_effectiveness || 'Not Assessed',
        design_effectiveness_score: control.design_effectiveness_score || 0,
        operating_effectiveness_score: control.operating_effectiveness_score || 0,
        last_tested_date: control.last_tested_date || '',
        next_testing_due: control.next_testing_due || calculateNextTestingDue(control.testing_frequency),
        testing_frequency: control.testing_frequency || 'Quarterly',
        current_status: control.current_status || 'Not Tested',
        owner: control.owner || 'Unassigned',
        process_step_name: control.process_step_name || 'Unknown Process',
        risk_count: control.risk_count || 0,
        gaps: control.gaps || [],
        testing_history: control.testing_history || [],
        remediation_actions: control.remediation_actions || []
      }));

      const realMetrics: ProcessMetrics = dashboardMetrics || {
        total_controls: realControls.length,
        effective_controls: realControls.filter(c => c.current_status === 'Effective').length,
        testing_overdue: realControls.filter(c => new Date(c.next_testing_due) < new Date()).length,
        open_gaps: realControls.reduce((sum, c) => sum + c.gaps.length, 0),
        pending_actions: realControls.reduce((sum, c) => sum + c.remediation_actions.length, 0),
        avg_effectiveness_score: realControls.length > 0
          ? Math.round(realControls.reduce((sum, c) => sum + c.operating_effectiveness_score, 0) / realControls.length)
          : 0,
        trend_direction: dashboardMetrics?.trend_direction || 'stable',
        trend_percentage: dashboardMetrics?.trend_percentage || 0
      };

      setControls(realControls);
      setProcessMetrics(realMetrics);

    } catch (error) {
      console.error('Error fetching process owner data:', error);
    }
  };

  const calculateNextTestingDue = (frequency: string): string => {
    const now = new Date();
    let daysToAdd = 90;

    switch (frequency?.toLowerCase()) {
      case 'daily':
        daysToAdd = 1;
        break;
      case 'weekly':
        daysToAdd = 7;
        break;
      case 'monthly':
        daysToAdd = 30;
        break;
      case 'quarterly':
        daysToAdd = 90;
        break;
      case 'semi-annually':
        daysToAdd = 180;
        break;
      case 'annually':
        daysToAdd = 365;
        break;
      default:
        daysToAdd = 90;
    }

    const nextDate = new Date(now.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
    return nextDate.toISOString().split('T')[0];
  };

  const refreshDashboard = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setRefreshing(false);
    toastService.success('Dashboard refreshed');
  };

  const exportDashboard = async () => {
    try {
      console.log('📤 Exporting dashboard for project:', projectId);

      const exportParams = {
        timeframe: selectedTimeframe,
        format: 'pdf',
        ...(projectId && { project_id: projectId })
      };

      const response = await api.get('/api/executive-dashboard/export', {
        params: exportParams,
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `executive-dashboard-${selectedTimeframe}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toastService.success('Dashboard exported successfully');
    } catch (error) {
      console.error('Failed to export dashboard:', error);
      toastService.error('Failed to export dashboard');
    }
  };

  const handleControlTesting = async (controlId: string, testData: any) => {
    try {
      await api.post(`/api/process-owner-dashboard/controls/${controlId}/test`, {
        test_type: testData.test_type || 'manual',
        result: testData.result || 'Effective',
        effectiveness_score: testData.effectiveness_score || 85,
        findings: testData.findings || '',
        test_date: testData.test_date || new Date().toISOString()
      });

      await fetchProcessOwnerData();
      setTestingDialogOpen(false);
      toastService.success('Control test recorded successfully');

    } catch (error) {
      console.error('Error recording control test:', error);
      toastService.error('Failed to record control test');
    }
  };

  const handleGapCreation = async (gapData: any) => {
    try {
      await api.post('/api/process-owner-dashboard/gaps', {
        control_id: gapData.control_id || selectedControl?.id,
        gap_type: gapData.gap_type || 'Operating Gap',
        description: gapData.description || '',
        severity: gapData.severity || 'Medium',
        remediation_plan: gapData.remediation_plan || '',
        target_resolution_date: gapData.target_resolution_date || '',
        assigned_to: gapData.assigned_to || ''
      });

      await fetchProcessOwnerData();
      setGapDialogOpen(false);
      toastService.success('Control gap recorded successfully');

    } catch (error) {
      console.error('Error creating gap:', error);
      toastService.error('Failed to record control gap');
    }
  };

  const fetchRiskExpertAnalytics = async () => {
    try {
      if (!projectId) return;

      const response = await api.get(`/api/risk-expert-dashboard/analytics/${projectId}`, {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          metrics: selectedMetrics.join(','),
          time_horizon: modelingParams.time_horizon.toString(),
          confidence_level: modelingParams.confidence_level.toString(),
          iterations: modelingParams.iterations.toString(),
          scenario_type: modelingParams.scenario_type,
          correlation_threshold: modelingParams.correlation_threshold.toString(),
          volatility_adjustment: modelingParams.volatility_adjustment.toString()
        }
      });

      const data = response.data;
      if (data.success) {
        setRiskAnalytics(data.analytics);
      }

    } catch (error) {
      console.error('Error fetching risk expert analytics:', error);
      toastService.error('Failed to load risk expert analytics');
    }
  };

  const runMonteCarloSimulation = async () => {
    try {
      setLoading(true);

      if (!projectId) {
        toastService.error('Project ID is required for simulation');
        setLoading(false);
        return;
      }

      const response = await api.post(`/api/risk-expert-dashboard/monte-carlo/${projectId}`, {
        iterations: modelingParams.iterations,
        confidence_level: modelingParams.confidence_level,
        time_horizon: modelingParams.time_horizon,
        scenario_type: modelingParams.scenario_type
      });

      const data = response.data;

      if (data.success) {
        if (riskAnalytics) {
          setRiskAnalytics({
            ...riskAnalytics,
            monte_carlo_results: data.results
          });
        }

        toastService.success(`Monte Carlo simulation completed with ${data.results.iterations.toLocaleString()} iterations`);
      } else {
        throw new Error(data.message || 'Simulation failed');
      }

      setLoading(false);

    } catch (error) {
      console.error('Error running Monte Carlo simulation:', error);
      toastService.error(error instanceof Error ? error.message : 'Failed to run Monte Carlo simulation');
      setLoading(false);
    }
  };

  const runScenarioAnalysis = async (scenarioType: string) => {
    try {
      setLoading(true);

      if (!projectId) {
        toastService.error('Project ID is required for scenario analysis');
        setLoading(false);
        return;
      }

      const response = await api.post(`/api/risk-expert-dashboard/scenario-analysis/${projectId}`, {
        scenario_type: scenarioType,
        parameters: {
          time_horizon: modelingParams.time_horizon,
          confidence_level: modelingParams.confidence_level,
          volatility_adjustment: modelingParams.volatility_adjustment
        }
      });

      const data = response.data;

      if (data.success) {
        if (riskAnalytics) {
          setRiskAnalytics({
            ...riskAnalytics,
            scenario_analysis: data.scenarios
          });
        }

        toastService.success(`${scenarioType.charAt(0).toUpperCase() + scenarioType.slice(1)} scenario analysis completed`);
      } else {
        throw new Error(data.message || 'Scenario analysis failed');
      }

      setLoading(false);

    } catch (error) {
      console.error('Error running scenario analysis:', error);
      toastService.error(error instanceof Error ? error.message : 'Failed to run scenario analysis');
      setLoading(false);
    }
  };

  const handleAddControl = async (controlData: any) => {
    try {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      await api.post('/api/process-step-controls', {
        ...controlData,
        project_id: parseInt(projectId.toString()),
        control_id: controlData.control_id || `CTRL_${Date.now()}`,
        effectiveness_score: controlData.operating_effectiveness_score || 85,
        control_effectiveness: controlData.operating_effectiveness || 'Effective',
        control_nature: controlData.automation_level || 'Manual'
      });

      await fetchProcessOwnerData();
      setAddControlModalOpen(false);
      toastService.success('Control added successfully');

    } catch (error) {
      console.error('Error adding control:', error);
      toastService.error(error instanceof Error ? error.message : 'Failed to add control');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing': case 'worsening': return <TrendingUp className="h-4 w-4 text-red-500" />;
      case 'decreasing': case 'improving': return <TrendingDown className="h-4 w-4 text-green-500" />;
      default: return <Activity className="h-4 w-4 text-gray-500" />;
    }
  };

  const getComplianceStatusColor = (score: number) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Show project selection if no project is available
  if (!projectId && !loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Project</h3>
          <p className="text-gray-600">Please select a project to view the executive dashboard.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">
          Loading executive dashboard{projectId ? ` for project ${projectId}` : ''}...
        </span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Building2 className="h-6 w-6 mr-3 text-[#221551]" />
              Executive Risk Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              {projectId
                ? `Project ${currentProject?.name || projectId} risk posture and key performance indicators`
                : 'Enterprise-wide risk posture and key performance indicators'
              }
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Timeframe Selector */}
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
            >
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
              <option value="1y">Last Year</option>
            </select>

            {/* Action Buttons */}
            {(selectedView === 'testing' || selectedView === 'gaps' || selectedView === 'trends') && (
              <button
                onClick={() => setAddControlModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Control
              </button>
            )}

            <button
              onClick={refreshDashboard}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* View Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <div className="flex space-x-1 overflow-x-auto px-2 premium-scrollbar">
            {[
              { id: 'overview', label: 'Overview', icon: Gauge },
              { id: 'risks', label: 'Risk Analysis', icon: AlertTriangle },
              { id: 'controls', label: 'Controls', icon: Shield },
              { id: 'compliance', label: 'Compliance', icon: Target },
              { id: 'testing', label: 'Testing Schedule', icon: Clock },
              { id: 'gaps', label: 'Control Gaps', icon: Bug },
              { id: 'trends', label: 'Performance Trends', icon: BarChart3 },
              { id: 'risk_analytics', label: 'Risk Analytics', icon: Activity },
              { id: 'correlation', label: 'Correlation Analysis', icon: ScatterChart },
              { id: 'scenario', label: 'Scenario Modeling', icon: Settings },
              { id: 'predictive', label: 'Predictive Models', icon: Brain },
              { id: 'exports', label: 'Exports', icon: Download }
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = selectedView === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedView(tab.id as any)}
                  className={`group relative flex items-center gap-2 py-2.5 px-4 font-semibold text-sm transition-all duration-300 whitespace-nowrap rounded-t-lg ${
                    isActive
                      ? 'text-[#221551] bg-gradient-to-b from-blue-50 to-transparent'
                      : 'text-gray-600 hover:text-[#221551] hover:bg-gray-50'
                  }`}
                >
                  <Icon className={`h-4 w-4 transition-all duration-300 ${
                    isActive
                      ? 'text-[#57C8F4]'
                      : 'text-gray-400 group-hover:text-[#57C8F4]'
                  }`} />
                  <span className="relative">
                    {tab.label}
                  </span>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#57C8F4] to-[#30A8CF] rounded-full shadow-lg shadow-blue-300/50"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Overview Tab */}
      {selectedView === 'overview' && (
        <div className="space-y-6">
          {/* Key Performance Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Risk KPI */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Risks</p>
                  <p className="text-2xl font-bold text-gray-900">{riskMetrics?.total_risks || 0}</p>
                  <div className="flex items-center mt-2">
                    {getTrendIcon(riskMetrics?.risk_trend || 'stable')}
                    <span className="text-sm text-gray-600 ml-1">
                      {riskMetrics?.risk_trend || 'stable'}
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-red-50 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>

            {/* Control KPI */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Control Effectiveness</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {controlMetrics?.control_effectiveness_avg || 0}%
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-600">
                      {controlMetrics?.effective_controls || 0} of {controlMetrics?.total_controls || 0} effective
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-green-50 rounded-full">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* Compliance KPI */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Compliance Score</p>
                  <p className={`text-2xl font-bold ${getComplianceStatusColor(complianceMetrics?.compliance_score || 0)}`}>
                    {complianceMetrics?.compliance_score || 0}%
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-600">
                      {complianceMetrics?.regulatory_violations || 0} violations
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <Target className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Business KPI */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Coverage</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {businessMetrics ? Math.round((businessMetrics.business_units_assessed / businessMetrics.total_business_units) * 100) : 0}%
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-600">
                      {businessMetrics?.business_units_assessed || 0} of {businessMetrics?.total_business_units || 0} units
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <Building2 className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Risk Distribution Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <PieChart className="h-5 w-5 mr-2" />
                Risk Distribution
              </h3>
              <div className="space-y-4">
                {[
                  { level: 'Critical', count: riskMetrics?.critical_risks || 0, color: 'bg-red-500' },
                  { level: 'High', count: riskMetrics?.high_risks || 0, color: 'bg-orange-500' },
                  { level: 'Medium', count: riskMetrics?.medium_risks || 0, color: 'bg-yellow-500' },
                  { level: 'Low', count: riskMetrics?.low_risks || 0, color: 'bg-green-500' }
                ].map(item => {
                  const percentage = riskMetrics?.total_risks ?
                    Math.round((item.count / riskMetrics.total_risks) * 100) : 0;

                  return (
                    <div key={item.level} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${item.color} mr-3`}></div>
                        <span className="text-sm font-medium text-gray-700">{item.level}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-600 mr-2">{item.count}</span>
                        <span className="text-xs text-gray-500">({percentage}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Alerts */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  Recent Alerts
                </h3>
                <button
                  onClick={() => setShowAllAlerts(!showAllAlerts)}
                  className="text-[#57C8F4] hover:text-[#30A8CF] text-sm font-medium flex items-center"
                >
                  {showAllAlerts ? 'Show Less' : `View All (${alertItems.length})`}
                  <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${showAllAlerts ? 'rotate-90' : ''}`} />
                </button>
              </div>

              <div className="space-y-3">
                {(showAllAlerts ? alertItems : alertItems.slice(0, 5)).map(alert => (
                  <div key={alert.id} className="flex items-start p-3 bg-gray-50 rounded-lg">
                    <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${
                      alert.severity === 'critical' ? 'bg-red-500' :
                      alert.severity === 'high' ? 'bg-orange-500' :
                      alert.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {alert.title}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {alert.business_unit} • {new Date(alert.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}

                {alertItems.length === 0 && (
                  <div className="text-center py-6 text-gray-500">
                    No recent alerts
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Risk Appetite Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Gauge className="h-5 w-5 mr-2" />
              Risk Appetite Status
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">
                  {riskMetrics?.risk_appetite_breaches || 0}
                </div>
                <div className="text-sm text-gray-600">Threshold Breaches</div>
                <div className="text-xs text-gray-500 mt-1">Last 30 days</div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {riskMetrics?.avg_risk_score || 0}
                </div>
                <div className="text-sm text-gray-600">Average Risk Score</div>
                <div className="text-xs text-gray-500 mt-1">Across all risks</div>
              </div>

              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {businessMetrics?.incidents_reported || 0}
                </div>
                <div className="text-sm text-gray-600">Incidents Reported</div>
                <div className="text-xs text-gray-500 mt-1">
                  Trend: {businessMetrics?.incident_trend || 'stable'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Analysis Tab */}
      {selectedView === 'risks' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Heat Map */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Risk Heat Map
              </h3>
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="text-xs font-medium text-gray-500 text-center">Impact →</div>
                <div className="text-xs font-medium text-gray-500 text-center">Low</div>
                <div className="text-xs font-medium text-gray-500 text-center">Medium</div>
                <div className="text-xs font-medium text-gray-500 text-center">High</div>
                <div className="text-xs font-medium text-gray-500">High</div>
                <div className="h-8 bg-yellow-200 rounded flex items-center justify-center text-xs">2</div>
                <div className="h-8 bg-orange-300 rounded flex items-center justify-center text-xs">5</div>
                <div className="h-8 bg-red-400 rounded flex items-center justify-center text-xs">{riskMetrics?.critical_risks || 0}</div>
                <div className="text-xs font-medium text-gray-500">Medium</div>
                <div className="h-8 bg-green-200 rounded flex items-center justify-center text-xs">3</div>
                <div className="h-8 bg-yellow-300 rounded flex items-center justify-center text-xs">{riskMetrics?.medium_risks || 0}</div>
                <div className="h-8 bg-orange-400 rounded flex items-center justify-center text-xs">{riskMetrics?.high_risks || 0}</div>
                <div className="text-xs font-medium text-gray-500">Low</div>
                <div className="h-8 bg-green-300 rounded flex items-center justify-center text-xs">{riskMetrics?.low_risks || 0}</div>
                <div className="h-8 bg-green-200 rounded flex items-center justify-center text-xs">1</div>
                <div className="h-8 bg-yellow-200 rounded flex items-center justify-center text-xs">1</div>
              </div>
            </div>

            {/* Risk Appetite Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Gauge className="h-5 w-5 mr-2" />
                Risk Appetite Analysis
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-red-800">Breaches</p>
                    <p className="text-sm text-red-600">Risks exceeding appetite</p>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {riskMetrics?.risk_appetite_breaches || 0}
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-800">Average Score</p>
                    <p className="text-sm text-blue-600">Overall risk rating</p>
                  </div>
                  <div className="text-2xl font-bold text-blue-600">
                    {riskMetrics?.avg_risk_score || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Trends */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Risk Trend Analysis
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {riskMetrics?.total_risks || 0}
                </div>
                <div className="text-sm text-gray-600">Total Risks</div>
                <div className="flex items-center justify-center mt-2">
                  {getTrendIcon(riskMetrics?.risk_trend || 'stable')}
                  <span className="text-xs text-gray-500 ml-1">
                    {riskMetrics?.risk_trend || 'stable'}
                  </span>
                </div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600 mb-1">
                  {((riskMetrics?.critical_risks || 0) + (riskMetrics?.high_risks || 0))}
                </div>
                <div className="text-sm text-gray-600">High Priority</div>
                <div className="text-xs text-gray-500 mt-1">Critical + High</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {riskMetrics?.total_risks ?
                    Math.round(((riskMetrics.total_risks - riskMetrics.critical_risks - riskMetrics.high_risks) / riskMetrics.total_risks) * 100)
                    : 0}%
                </div>
                <div className="text-sm text-gray-600">Within Tolerance</div>
                <div className="text-xs text-gray-500 mt-1">Medium + Low</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls Tab */}
      {selectedView === 'controls' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Control Effectiveness Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Shield className="h-5 w-5 mr-2" />
                Control Effectiveness
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Total Controls</span>
                  <span className="text-lg font-bold text-gray-900">{controlMetrics?.total_controls || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Effective Controls</span>
                  <span className="text-lg font-bold text-green-600">{controlMetrics?.effective_controls || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Ineffective Controls</span>
                  <span className="text-lg font-bold text-red-600">{controlMetrics?.ineffective_controls || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Need Review</span>
                  <span className="text-lg font-bold text-yellow-600">{controlMetrics?.controls_needing_review || 0}</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Overall Effectiveness</span>
                    <span className="text-xl font-bold text-blue-600">{controlMetrics?.control_effectiveness_avg || 0}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Control Testing Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Testing Status
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div>
                    <p className="font-medium text-green-800">Recently Tested</p>
                    <p className="text-sm text-green-600">Last 30 days</p>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    {controlMetrics?.controls_tested_last_30_days || 0}
                  </div>
                </div>
                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="font-medium text-yellow-800">Due for Testing</p>
                    <p className="text-sm text-yellow-600">Next 30 days</p>
                  </div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {Math.max(0, (controlMetrics?.total_controls || 0) - (controlMetrics?.controls_tested_last_30_days || 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Control Performance Chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Control Performance Distribution
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {controlMetrics?.effective_controls || 0}
                </div>
                <div className="text-sm text-gray-600">Effective</div>
                <div className="text-xs text-gray-500">
                  {controlMetrics?.total_controls ?
                    Math.round((controlMetrics.effective_controls / controlMetrics.total_controls) * 100) : 0}%
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 mb-1">
                  {controlMetrics?.controls_needing_review || 0}
                </div>
                <div className="text-sm text-gray-600">Needs Review</div>
                <div className="text-xs text-gray-500">
                  {controlMetrics?.total_controls ?
                    Math.round((controlMetrics.controls_needing_review / controlMetrics.total_controls) * 100) : 0}%
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-red-600 mb-1">
                  {controlMetrics?.ineffective_controls || 0}
                </div>
                <div className="text-sm text-gray-600">Ineffective</div>
                <div className="text-xs text-gray-500">
                  {controlMetrics?.total_controls ?
                    Math.round((controlMetrics.ineffective_controls / controlMetrics.total_controls) * 100) : 0}%
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {controlMetrics?.control_effectiveness_avg || 0}%
                </div>
                <div className="text-sm text-gray-600">Avg Score</div>
                <div className="text-xs text-gray-500">Overall</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {selectedView === 'compliance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance Score Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Target className="h-5 w-5 mr-2" />
                Compliance Score
              </h3>
              <div className="text-center">
                <div className={`text-5xl font-bold mb-2 ${getComplianceStatusColor(complianceMetrics?.compliance_score || 0)}`}>
                  {complianceMetrics?.compliance_score || 0}%
                </div>
                <div className="text-sm text-gray-600 mb-4">Overall Compliance Rating</div>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  (complianceMetrics?.compliance_score || 0) >= 95 ? 'bg-green-100 text-green-800' :
                  (complianceMetrics?.compliance_score || 0) >= 85 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {(complianceMetrics?.compliance_score || 0) >= 95 ? 'Excellent' :
                   (complianceMetrics?.compliance_score || 0) >= 85 ? 'Good' : 'Needs Improvement'}
                </div>
              </div>
            </div>

            {/* Audit Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Eye className="h-5 w-5 mr-2" />
                Audit Status
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Open Findings</span>
                  <span className="text-lg font-bold text-red-600">{complianceMetrics?.audit_findings_open || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Regulatory Violations</span>
                  <span className="text-lg font-bold text-orange-600">{complianceMetrics?.regulatory_violations || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Certification Status</span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    complianceMetrics?.certification_status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {complianceMetrics?.certification_status || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Next Audit</span>
                  <span className="text-sm text-gray-900">
                    {complianceMetrics?.next_audit_date ?
                      new Date(complianceMetrics.next_audit_date).toLocaleDateString() : 'Not scheduled'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Management */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Policy Management
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {complianceMetrics?.policies_updated_last_quarter || 0}
                </div>
                <div className="text-sm text-gray-600">Policies Updated</div>
                <div className="text-xs text-gray-500 mt-1">Last Quarter</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {complianceMetrics?.compliance_score || 0}%
                </div>
                <div className="text-sm text-gray-600">Policy Compliance</div>
                <div className="text-xs text-gray-500 mt-1">Current Rate</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600 mb-1">
                  {Math.max(0, 10 - (complianceMetrics?.policies_updated_last_quarter || 0))}
                </div>
                <div className="text-sm text-gray-600">Due for Review</div>
                <div className="text-xs text-gray-500 mt-1">Next 30 days</div>
              </div>
            </div>
          </div>

          {/* Compliance Trends */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Compliance Trends
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Regulatory Compliance</p>
                  <p className="text-sm text-gray-600">{complianceMetrics?.regulatory_violations || 0} violations this period</p>
                </div>
                <div className="flex items-center">
                  {(complianceMetrics?.regulatory_violations || 0) === 0 ?
                    <CheckCircle className="h-5 w-5 text-green-500" /> :
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  }
                </div>
              </div>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Audit Readiness</p>
                  <p className="text-sm text-gray-600">{complianceMetrics?.audit_findings_open || 0} open findings</p>
                </div>
                <div className="flex items-center">
                  {(complianceMetrics?.audit_findings_open || 0) === 0 ?
                    <CheckCircle className="h-5 w-5 text-green-500" /> :
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Testing Schedule Tab */}
      {selectedView === 'testing' && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Testing Schedule
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Upcoming Tests */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Upcoming Tests
                </h4>
                <div className="space-y-3">
                  {controls.filter(c => new Date(c.next_testing_due) >= new Date()).slice(0, 5).map(control => (
                    <div key={control.id} className="flex items-center justify-between p-3 bg-white rounded border">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{control.control_name}</p>
                        <p className="text-xs text-gray-600">{control.testing_frequency} testing</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-blue-600">{format(new Date(control.next_testing_due), 'MMM dd')}</p>
                        <p className="text-xs text-gray-500">Due</p>
                      </div>
                    </div>
                  ))}
                  {controls.filter(c => new Date(c.next_testing_due) >= new Date()).length === 0 && (
                    <p className="text-gray-500 text-center py-4">No upcoming tests scheduled</p>
                  )}
                </div>
              </div>

              {/* Overdue Tests */}
              <div className="bg-red-50 rounded-lg p-4">
                <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2 text-red-500" />
                  Overdue Tests
                </h4>
                <div className="space-y-3">
                  {controls.filter(c => new Date(c.next_testing_due) < new Date()).map(control => (
                    <div key={control.id} className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{control.control_name}</p>
                        <p className="text-xs text-gray-600">{control.testing_frequency} testing</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-red-600">{format(new Date(control.next_testing_due), 'MMM dd')}</p>
                        <p className="text-xs text-red-500">Overdue</p>
                      </div>
                    </div>
                  ))}
                  {controls.filter(c => new Date(c.next_testing_due) < new Date()).length === 0 && (
                    <p className="text-gray-500 text-center py-4">No overdue tests</p>
                  )}
                </div>
              </div>
            </div>

            {/* Testing Calendar */}
            <div className="bg-white rounded-lg border p-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Testing Calendar - {format(new Date(), 'MMMM yyyy')}</h4>
              <div className="grid grid-cols-7 gap-2 text-center text-xs font-medium text-gray-500 mb-2">
                <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {(() => {
                  const today = new Date();
                  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

                  const startDate = new Date(firstDayOfMonth);
                  startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

                  return Array.from({ length: 42 }, (_, i) => {
                    const date = new Date(startDate);
                    date.setDate(startDate.getDate() + i);

                    const dateStr = format(date, 'yyyy-MM-dd');
                    const isCurrentMonth = date.getMonth() === today.getMonth();
                    const isToday = format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');

                    const testsOnThisDate = controls.filter(c =>
                      format(new Date(c.next_testing_due), 'yyyy-MM-dd') === dateStr
                    );

                    const hasTest = testsOnThisDate.length > 0;
                    const isOverdue = hasTest && testsOnThisDate.some(c => new Date(c.next_testing_due) < today);

                    return (
                      <div
                        key={i}
                        className={`h-8 flex items-center justify-center text-xs rounded relative ${
                          !isCurrentMonth
                            ? 'text-gray-300 bg-gray-50'
                            : isToday
                            ? 'bg-blue-500 text-white font-bold'
                            : hasTest
                            ? isOverdue
                              ? 'bg-red-200 text-red-800 font-medium'
                              : 'bg-green-200 text-green-800 font-medium'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={hasTest ? `${testsOnThisDate.length} test(s) due: ${testsOnThisDate.map(c => c.control_name).join(', ')}` : ''}
                      >
                        {date.getDate()}
                        {hasTest && (
                          <div className="absolute top-0 right-0 w-2 h-2 bg-current rounded-full opacity-70"></div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              <div className="flex items-center justify-center gap-4 mt-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>Today</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-200 rounded"></div>
                  <span>Tests Due</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-200 rounded"></div>
                  <span>Overdue</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Control Gaps Tab */}
      {selectedView === 'gaps' && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Control Gaps Management
              </h3>
              <button
                onClick={() => setGapDialogOpen(true)}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Report Gap
              </button>
            </div>

            {/* Gaps Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">
                  {controls.reduce((sum, control) => sum + control.gaps.length, 0)}
                </div>
                <div className="text-sm text-gray-600">Total Gaps</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600 mb-1">
                  {controls.reduce((sum, control) => sum + control.gaps.filter(g => g.severity === 'High').length, 0)}
                </div>
                <div className="text-sm text-gray-600">High Severity</div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600 mb-1">
                  {controls.reduce((sum, control) => sum + control.gaps.filter(g => g.severity === 'Medium').length, 0)}
                </div>
                <div className="text-sm text-gray-600">Medium Severity</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {controls.reduce((sum, control) => sum + control.gaps.filter(g => g.status === 'Open').length, 0)}
                </div>
                <div className="text-sm text-gray-600">Open Status</div>
              </div>
            </div>

            {/* Gaps List */}
            <div className="space-y-4">
              {controls.flatMap(control =>
                control.gaps.map(gap => ({
                  ...gap,
                  control_name: control.control_name,
                  process_step: control.process_step_name
                }))
              ).map(gap => (
                <div key={gap.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3 ${
                          gap.severity === 'High' ? 'bg-red-100 text-red-800' :
                          gap.severity === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {gap.severity}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          gap.status === 'Open' ? 'bg-red-100 text-red-800' :
                          gap.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {gap.status}
                        </span>
                      </div>
                      <h4 className="text-md font-medium text-gray-900 mb-2">{gap.gap_type}</h4>
                      <p className="text-sm text-gray-600 mb-2">{gap.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Control:</span> {gap.control_name}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Process:</span> {gap.process_step}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Assigned to:</span> {gap.assigned_to}
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Target Resolution:</span> {format(new Date(gap.target_resolution_date), 'MMM dd, yyyy')}
                        </div>
                      </div>
                      {gap.remediation_plan && (
                        <div className="mt-3 p-3 bg-gray-50 rounded">
                          <span className="font-medium text-gray-700">Remediation Plan:</span>
                          <p className="text-sm text-gray-600 mt-1">{gap.remediation_plan}</p>
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex space-x-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              {controls.every(control => control.gaps.length === 0) && (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Control Gaps Found</h3>
                  <p className="text-gray-600">All controls are operating effectively with no identified gaps.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Performance Trends Tab */}
      {selectedView === 'trends' && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Performance Trends
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-2">Control Effectiveness Trend</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart
                    data={controls.map(control => ({
                      name: control.control_name.substring(0, 20) + '...',
                      effectiveness: control.operating_effectiveness_score
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line type="monotone" dataKey="effectiveness" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-2">Testing Status Distribution</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={[
                        { name: 'On Schedule', value: controls.filter(c => new Date(c.next_testing_due) >= new Date()).length },
                        { name: 'Overdue', value: controls.filter(c => new Date(c.next_testing_due) < new Date()).length }
                      ]}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#4caf50" />
                      <Cell fill="#f44336" />
                    </Pie>
                    <RechartsTooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Analytics Tab */}
      {selectedView === 'risk_analytics' && riskAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Risk Distribution Analysis
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={riskAnalytics.risk_distribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <RechartsTooltip />
                    <Bar yAxisId="left" dataKey="count" fill="#8884d8" />
                    <Line yAxisId="right" type="monotone" dataKey="avg_score" stroke="#ff7300" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Risk Heat Map
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <RechartsScatterChart data={riskAnalytics.risk_heat_map}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="likelihood" name="Likelihood" />
                    <YAxis dataKey="impact" name="Impact" />
                    <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Scatter dataKey="count" fill="#8884d8" />
                  </RechartsScatterChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Risk Trends Over Time
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={riskAnalytics.risk_trends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="high_risks" stackId="1" stroke="#ff4444" fill="#ff4444" />
                    <Area type="monotone" dataKey="medium_risks" stackId="1" stroke="#ffaa00" fill="#ffaa00" />
                    <Area type="monotone" dataKey="low_risks" stackId="1" stroke="#44ff44" fill="#44ff44" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Correlation Analysis Tab */}
      {selectedView === 'correlation' && riskAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Risk Correlation Matrix
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk A</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk B</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correlation</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Significance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Strength</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {riskAnalytics.correlation_matrix.map((correlation, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {correlation.risk_a}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {correlation.risk_b}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    Math.abs(correlation.correlation) > 0.5 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.abs(correlation.correlation) * 100}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-900">
                                {correlation.correlation.toFixed(3)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              correlation.significance < 0.05 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {correlation.significance.toFixed(3)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {Math.abs(correlation.correlation) > 0.7 ? 'Strong' :
                             Math.abs(correlation.correlation) > 0.3 ? 'Moderate' : 'Weak'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Correlation Network
                </h3>
                <div className="h-96 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">Network visualization would be rendered here</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Modeling Tab */}
      {selectedView === 'scenario' && riskAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Scenario Analysis Results
                  </h3>
                  <div className="space-x-2">
                    <button
                      onClick={() => runScenarioAnalysis('optimistic')}
                      className="px-3 py-1 text-sm border border-green-300 text-green-600 rounded hover:bg-green-50"
                    >
                      Optimistic
                    </button>
                    <button
                      onClick={() => runScenarioAnalysis('pessimistic')}
                      className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded hover:bg-red-50"
                    >
                      Pessimistic
                    </button>
                    <button
                      onClick={() => runScenarioAnalysis('stress')}
                      className="px-3 py-1 text-sm border border-yellow-300 text-yellow-600 rounded hover:bg-yellow-50"
                    >
                      Stress Test
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scenario</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Probability</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impact Range</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk Change</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Required</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {riskAnalytics.scenario_analysis.map((scenario, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {scenario.scenario}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              scenario.probability > 0.3 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {(scenario.probability * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {scenario.impact_range[0].toFixed(1)} - {scenario.impact_range[1].toFixed(1)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {scenario.risk_change > 0 ? (
                                <TrendingUp className="w-4 h-4 text-red-500 mr-1" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-green-500 mr-1" />
                              )}
                              <span className={`text-sm ${
                                scenario.risk_change > 0 ? 'text-red-600' : 'text-green-600'
                              }`}>
                                {scenario.risk_change > 0 ? '+' : ''}{scenario.risk_change.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {scenario.risk_change > 20 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Critical
                              </span>
                            ) : scenario.risk_change > 10 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Monitor
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Normal
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Monte Carlo Results
                  </h3>
                  <button
                    onClick={runMonteCarloSimulation}
                    className="flex items-center px-3 py-1 text-sm border border-blue-300 text-blue-600 rounded hover:bg-blue-50"
                    disabled={loading}
                  >
                    <Play className="w-3 h-3 mr-1" />
                    Run
                  </button>
                </div>
                {riskAnalytics.monte_carlo_results && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">
                      {riskAnalytics.monte_carlo_results.iterations.toLocaleString()} iterations
                    </p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-lg font-semibold">
                          Mean: {riskAnalytics.monte_carlo_results.mean.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">
                          Std Dev: {riskAnalytics.monte_carlo_results.std_dev.toFixed(2)}
                        </p>
                      </div>
                      <hr className="my-3" />
                      <div>
                        <p className="font-medium text-gray-900 mb-2">Percentiles:</p>
                        {Object.entries(riskAnalytics.monte_carlo_results.percentiles).map(([percentile, value]) => (
                          <div key={percentile} className="flex justify-between items-center py-1">
                            <span className="text-sm text-gray-600">{percentile}th:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {value.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Predictive Models Tab */}
      {selectedView === 'predictive' && riskAnalytics && (
        <div>
          <div className="bg-white rounded-lg shadow border">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Predictive Risk Models
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {riskAnalytics.predictive_models.map((model, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-lg font-medium text-gray-900">{model.model_name}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        model.accuracy > 80 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {model.accuracy}% accuracy
                      </span>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={model.predictions}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis />
                        <RechartsTooltip />
                        <Line type="monotone" dataKey="predicted_risks" stroke="#8884d8" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exports Tab */}
      {selectedView === 'exports' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Comprehensive Export Options
            </h3>
            <p className="text-gray-600 mb-6">
              Export your RCSA data in various formats and report types for {currentProject?.name || 'this project'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  id: 'comprehensive_rcsa',
                  title: 'Comprehensive RCSA Report',
                  description: 'Complete risk and control assessment report including all components',
                  icon: BarChart3,
                  color: 'bg-blue-500'
                },
                {
                  id: 'executive_dashboard',
                  title: 'Executive Dashboard Export',
                  description: 'High-level summary for executive stakeholders',
                  icon: FileSpreadsheet,
                  color: 'bg-green-500'
                },
                {
                  id: 'control_assessment',
                  title: 'Control Assessment Report',
                  description: 'Detailed control effectiveness and testing results',
                  icon: Shield,
                  color: 'bg-purple-500'
                },
                {
                  id: 'risk_assessment',
                  title: 'Risk Assessment Report',
                  description: 'Complete risk analysis and residual risk calculations',
                  icon: AlertTriangle,
                  color: 'bg-red-500'
                },
                {
                  id: 'risk_expert_dashboard',
                  title: 'Risk Expert Analytics',
                  description: 'Advanced risk analytics, correlations, and predictive models',
                  icon: Brain,
                  color: 'bg-indigo-500'
                },
                {
                  id: 'control_gaps',
                  title: 'Control Gaps Report',
                  description: 'Identified control gaps and remediation actions',
                  icon: Bug,
                  color: 'bg-orange-500'
                },
                {
                  id: 'testing_history',
                  title: 'Testing History Report',
                  description: 'Complete control testing history and results',
                  icon: Clock,
                  color: 'bg-cyan-500'
                },
                {
                  id: 'compliance_report',
                  title: 'Compliance Report',
                  description: 'Regulatory compliance status and audit findings',
                  icon: Target,
                  color: 'bg-pink-500'
                }
              ].map((option) => {
                const Icon = option.icon;
                return (
                  <div
                    key={option.id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200"
                    onClick={() => {
                      setDefaultExportType(option.id);
                      setShowExportDialog(true);
                    }}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`${option.color} p-3 rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {option.title}
                        </h4>
                        <p className="text-gray-600 text-sm mb-4">
                          {option.description}
                        </p>
                        <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors">
                          <Download className="w-4 h-4 mr-2" />
                          Export
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Export Information</h3>
            <ul className="text-blue-800 text-sm space-y-1">
              <li>• All exports include project-specific data and current risk assessments</li>
              <li>• Excel exports include interactive charts and pivot tables</li>
              <li>• PDF exports are formatted for professional presentation</li>
              <li>• CSV exports provide raw data for further analysis</li>
              <li>• Customize export options including charts, raw data, and filenames</li>
            </ul>
          </div>
        </div>
      )}

      {/* Quick Actions Floating Button */}
      {(selectedView === 'risk_analytics' || selectedView === 'correlation' || selectedView === 'scenario' || selectedView === 'predictive') && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className="relative">
            <button
              onClick={runMonteCarloSimulation}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg"
              title="Run Monte Carlo Simulation"
            >
              <Zap className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddControlModal
        isOpen={addControlModalOpen}
        onClose={() => setAddControlModalOpen(false)}
        onSave={handleAddControl}
      />

      {/* Control Testing Modal */}
      {testingDialogOpen && selectedControl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Record Control Test</h2>
              <button
                onClick={() => setTestingDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Recording test for: <span className="font-medium">{selectedControl.control_name}</span>
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Result</label>
                <select
                  id="test-result"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Effective">Effective</option>
                  <option value="Partially Effective">Partially Effective</option>
                  <option value="Ineffective">Ineffective</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effectiveness Score (%)</label>
                <input
                  type="number"
                  id="effectiveness-score"
                  min="0"
                  max="100"
                  defaultValue="85"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Findings</label>
                <textarea
                  id="test-findings"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter test findings and observations..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setTestingDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const testResult = (document.getElementById('test-result') as HTMLSelectElement)?.value;
                  const effectivenessScore = (document.getElementById('effectiveness-score') as HTMLInputElement)?.value;
                  const findings = (document.getElementById('test-findings') as HTMLTextAreaElement)?.value;

                  handleControlTesting(selectedControl.id, {
                    test_type: 'observation',
                    result: testResult,
                    effectiveness_score: parseInt(effectivenessScore) || 85,
                    findings: findings || 'Test completed successfully',
                    test_date: new Date().toISOString()
                  });
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Record Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Control Gap Modal */}
      {gapDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Report Control Gap</h2>
              <button
                onClick={() => setGapDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Control *</label>
                <select
                  id="gap-control"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue={selectedControl?.id || ''}
                >
                  <option value="">Select a control...</option>
                  {controls.map((control) => (
                    <option key={control.id} value={control.id}>
                      {control.control_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gap Type</label>
                <select
                  id="gap-type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Operating Gap">Operating Gap</option>
                  <option value="Design Gap">Design Gap</option>
                  <option value="Documentation Gap">Documentation Gap</option>
                  <option value="Training Gap">Training Gap</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  id="gap-severity"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  id="gap-description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Describe the identified gap..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remediation Plan</label>
                <textarea
                  id="gap-remediation"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Describe the plan to address this gap..."
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setGapDialogOpen(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const controlId = (document.getElementById('gap-control') as HTMLSelectElement)?.value;
                  const gapType = (document.getElementById('gap-type') as HTMLSelectElement)?.value;
                  const severity = (document.getElementById('gap-severity') as HTMLSelectElement)?.value;
                  const description = (document.getElementById('gap-description') as HTMLTextAreaElement)?.value;
                  const remediation = (document.getElementById('gap-remediation') as HTMLTextAreaElement)?.value;

                  if (!controlId) {
                    alert('Please select a control');
                    return;
                  }

                  handleGapCreation({
                    control_id: parseInt(controlId),
                    gap_type: gapType,
                    severity: severity,
                    description: description,
                    remediation_plan: remediation,
                    target_resolution_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                    assigned_to: 'Current User'
                  });
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Report Gap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Export Dialog */}
      {showExportDialog && projectId && (
        <ComprehensiveExportDialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          projectId={projectId.toString()}
          defaultReportType={defaultExportType}
        />
      )}
    </div>
  );
};

export default ExecutiveDashboard;
